<app-header></app-header>

<section class="bv-emergency-access-page">
  <p class="bv-emergency-access-page__desc">
    {{ "emergencyAccessDesc" | i18n }}
    <a bitLink href="https://bitwarden.com/help/emergency-access/" target="_blank" rel="noreferrer">
      {{ "learnMoreAboutEmergencyAccess" | i18n }}
    </a>
  </p>

  @if (isOrganizationOwner) {
    <div class="bv-emergency-access-owner-warning">
      <bit-callout type="warning" title="{{ 'warning' | i18n }}">
        {{ "emergencyAccessOwnerWarning" | i18n }}
      </bit-callout>
    </div>
  }

  <section class="bv-emergency-access-section">
    <h2 class="bv-emergency-access-section__title">{{ "designatedEmergencyContacts" | i18n }}</h2>

    @if (!loaded) {
      <p class="bv-emergency-access-loading">
        <i class="bwi bwi-spinner bwi-spin" aria-hidden="true"></i>
        <span>{{ "loading" | i18n }}</span>
      </p>
    } @else if (grantedContacts?.length) {
      <div class="bv-emergency-access-table bv-emergency-access-table--granted">
        <div class="bv-emergency-access-table__header">
          <span>{{ "email" | i18n }}</span>
          <span>{{ "userAccess" | i18n }}</span>
          <span class="tw-sr-only">{{ "options" | i18n }}</span>
        </div>
        @for (c of grantedContacts; track c.id) {
          <div class="bv-emergency-access-table__row">
            <div class="bv-emergency-access-table__email">
              <div class="bv-emergency-access-table__email-main">
                <span>{{ c.email }}</span>
                @if (c.status === emergencyAccessStatusType.Invited) {
                  <span bitBadge>{{ "invited" | i18n }}</span>
                }
                @if (c.status === emergencyAccessStatusType.Accepted) {
                  <span bitBadge variant="warning">{{ "needsConfirmation" | i18n }}</span>
                }
                @if (c.status === emergencyAccessStatusType.RecoveryInitiated) {
                  <span bitBadge variant="warning">{{
                    "emergencyAccessRecoveryInitiated" | i18n
                  }}</span>
                }
                @if (c.status === emergencyAccessStatusType.RecoveryApproved) {
                  <span bitBadge variant="success">{{
                    "emergencyAccessRecoveryApproved" | i18n
                  }}</span>
                }
              </div>
              @if (c.name) {
                <span class="bv-emergency-access-table__name">{{ c.name }}</span>
              }
            </div>
            <span>{{ formatAccessType(c.type) }}</span>
            <div class="bv-emergency-access-table__actions">
              <button
                type="button"
                bitIconButton="bwi-ellipsis-v"
                class="bv-emergency-access-table__menu-btn"
                [bitMenuTriggerFor]="grantedContactOptions"
                [label]="'options' | i18n"
              ></button>
              <bit-menu #grantedContactOptions>
                @if (c.status === emergencyAccessStatusType.Confirmed) {
                  <button type="button" bitMenuItem (click)="requestAccess(c)">
                    <i class="bwi bwi-fw bwi-envelope" aria-hidden="true"></i>
                    {{ "requestAccess" | i18n }}
                  </button>
                }
                @if (
                  c.status === emergencyAccessStatusType.RecoveryApproved &&
                  c.type === emergencyAccessType.Takeover
                ) {
                  <button type="button" bitMenuItem (click)="takeover(c)">
                    <i class="bwi bwi-fw bwi-key" aria-hidden="true"></i>
                    {{ "takeover" | i18n }}
                  </button>
                }
                @if (
                  c.status === emergencyAccessStatusType.RecoveryApproved &&
                  c.type === emergencyAccessType.View
                ) {
                  <a bitMenuItem [routerLink]="c.id">
                    <i class="bwi bwi-fw bwi-eye" aria-hidden="true"></i>
                    {{ "view" | i18n }}
                  </a>
                }
                <button type="button" bitMenuItem (click)="remove(c)">
                  <i class="bwi bwi-fw bwi-close" aria-hidden="true"></i>
                  {{ "remove" | i18n }}
                </button>
              </bit-menu>
            </div>
          </div>
        }
      </div>
    } @else {
      <p class="bv-emergency-access-section__empty">{{ "noGrantedAccess" | i18n }}</p>
    }
  </section>

  <section class="bv-emergency-access-section">
    <div class="bv-emergency-access-section__header">
      <div class="bv-emergency-access-section__title-wrap">
        <h2 class="bv-emergency-access-section__title">{{ "trustedEmergencyContacts" | i18n }}</h2>
        <app-premium-badge></app-premium-badge>
      </div>
      <button
        type="button"
        class="bv-emergency-access-add-btn"
        [disabled]="!(canAccessPremium$ | async)"
        (click)="invite()"
      >
        <span class="bv-emergency-access-add-btn__icon" aria-hidden="true"></span>
        {{ "addEmergencyContact" | i18n }}
      </button>
    </div>

    @if (!loaded) {
      <p class="bv-emergency-access-loading">
        <i class="bwi bwi-spinner bwi-spin" aria-hidden="true"></i>
        <span>{{ "loading" | i18n }}</span>
      </p>
    } @else if (!trustedContacts?.length) {
      <p class="bv-emergency-access-section__empty">{{ "noTrustedContacts" | i18n }}</p>
    } @else {
      <div class="bv-emergency-access-table">
        <div class="bv-emergency-access-table__header">
          <span>{{ "email" | i18n }}</span>
          <span>{{ "userAccess" | i18n }}</span>
          <span>{{ "waitTime" | i18n }}</span>
          <span class="tw-sr-only">{{ "edit" | i18n }}</span>
        </div>
        @for (c of trustedContacts; track c.id) {
          <div class="bv-emergency-access-table__row">
            <div class="bv-emergency-access-table__email">
              <div class="bv-emergency-access-table__email-main">
                <button
                  type="button"
                  class="bv-emergency-access-table__email-link"
                  (click)="edit(c)"
                >
                  {{ c.email }}
                </button>
                @if (c.status === emergencyAccessStatusType.Invited) {
                  <span bitBadge variant="secondary">{{ "invited" | i18n }}</span>
                }
                @if (c.status === emergencyAccessStatusType.Accepted) {
                  <span bitBadge variant="warning">{{ "needsConfirmation" | i18n }}</span>
                }
                @if (c.status === emergencyAccessStatusType.RecoveryInitiated) {
                  <span bitBadge variant="warning">{{
                    "emergencyAccessRecoveryInitiated" | i18n
                  }}</span>
                }
                @if (c.status === emergencyAccessStatusType.RecoveryApproved) {
                  <span bitBadge>{{ "emergencyAccessRecoveryApproved" | i18n }}</span>
                }
              </div>
              @if (c.name) {
                <span class="bv-emergency-access-table__name">{{ c.name }}</span>
              }
              <div class="bv-emergency-access-status-actions">
                @if (c.status === emergencyAccessStatusType.Invited) {
                  <button type="button" (click)="reinvite(c)">
                    {{ "resendInvitation" | i18n }}
                  </button>
                }
                @if (c.status === emergencyAccessStatusType.Accepted) {
                  <button type="button" (click)="confirm(c)">{{ "confirm" | i18n }}</button>
                }
                @if (c.status === emergencyAccessStatusType.RecoveryInitiated) {
                  <button type="button" (click)="approve(c)">{{ "approve" | i18n }}</button>
                  <button type="button" (click)="reject(c)">{{ "reject" | i18n }}</button>
                }
                @if (c.status === emergencyAccessStatusType.RecoveryApproved) {
                  <button type="button" (click)="reject(c)">{{ "reject" | i18n }}</button>
                }
              </div>
            </div>
            <span>{{ formatAccessType(c.type) }}</span>
            <span>{{ formatWaitTime(c.waitTimeDays) }}</span>
            <div class="bv-emergency-access-table__actions">
              <button
                type="button"
                class="bv-emergency-access-table__edit-btn"
                [attr.aria-label]="'edit' | i18n"
                (click)="edit(c)"
              >
                <span class="bv-emergency-access-table__edit-btn-icon" aria-hidden="true"></span>
              </button>
            </div>
          </div>
        }
      </div>
    }
  </section>
</section>
